/**
 * Copyright (c) SAGE3 Development Team 2025. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Pak Kwan 2026-07-27: NarrativeLinkerMode for presentation layer
 * Enables linking any app types with narrative_order links (not just SageCells)
 */

import React, { useEffect, useMemo, useState } from 'react';

import { useToast } from '@chakra-ui/react';

import type { App } from '@sage3/applications/schema';
import { getAllowedLinkTypes, getNarrativePath } from '@sage3/applications/apps';
import { useHexColor, useLinkStore, useThrottleApps, useUIStore, useUserSettings, useHotkeys, setupApp, useAppStore, useUser, useCursorBoardPosition } from '@sage3/frontend';

/**
 * Narrative Linker overlay for presentation authoring:
 * 1. Select a source app (any type: Stickie, Image, PDF, Video, etc.)
 * 2. Highlight valid targets based on narrative_order constraints
 * 3. Click target to create narrative_order link
 * 4. Build linear presentation path through spatial content
 * 5. Phase 1B: Background-click gesture creates next step (Stickie) and links it
 */

type NarrativeLinkerModeProps = {
  roomId: string;
  boardId: string;
  onExit?: () => void;
};

export function NarrativeLinkerMode({ roomId, boardId, onExit }: NarrativeLinkerModeProps) {
  // Throttled list of apps and current link graph
  const apps = useThrottleApps(250);
  const links = useLinkStore((s) => s.links);
  const addLink = useLinkStore((s) => s.addLink);
  const clearLinkAppId = useLinkStore((s) => s.clearLinkAppId);
  const cacheLinkedAppId = useLinkStore((s) => s.cacheLinkedAppId);
  const { setPrimaryActionMode } = useUserSettings();

  // App creation
  const createApp = useAppStore((s) => s.create);
  const { user } = useUser();
  const { getBoardCursor } = useCursorBoardPosition();

  // Board viewport dimensions and zoom scale
  const { boardWidth, boardHeight, scale, boardPosition } = useUIStore((s) => ({
    boardWidth: s.boardWidth,
    boardHeight: s.boardHeight,
    scale: s.scale,
    boardPosition: s.boardPosition,
  }));

  const toast = useToast();

  // Get initial source ID from link store (set by AppToolbar when entering mode)
  const initialSourceId = useLinkStore((s) => s.linkedAppId);

  // Selection state: currently picked source ID (LOCAL state after initialization)
  // Initialize from link store on mount, then manage locally to avoid interference with run_order LinkerMode
  const [selectedSourceId, setSelectedSourceId] = useState<string>(initialSourceId || '');

  // Keep selectedSourceId in sync with initialSourceId ONLY if we haven't set our own value yet
  useEffect(() => {
    if (initialSourceId && !selectedSourceId) {
      setSelectedSourceId(initialSourceId);
    }
  }, [initialSourceId, selectedSourceId]);

  // Candidate app IDs (valid targets for narrative_order links)
  const [candidates, setCandidates] = useState<string[]>([]);

  // Colors
  const green = useHexColor('green');
  const teal = useHexColor('teal');

  // Prepare svg boxes for each app
  const boxes = useMemo(
    () =>
      apps.map((app: App) => ({
        id: app._id,
        position: app.data.position,
        size: app.data.size,
      })),
    [apps]
  );

  // On mount or apps/links change: compute valid targets
  useEffect(() => {
    if (selectedSourceId) {
      const sourceApp = apps.find((a) => a._id === selectedSourceId);
      if (!sourceApp) {
        setSelectedSourceId('');
        setCandidates([]);
        clearLinkAppId(); // Also clear store when source not found
        return;
      }

      // KEY CHANGE: Get ALL apps as potential targets (not just SageCells)
      // Filter by narrative_order constraint (any app type can be target)
      const validTargets = apps.filter((targetApp) => {
        const allowedTypes = getAllowedLinkTypes(
          sourceApp,
          targetApp,
          links,
          'narrative_order'  // Explicit filter for narrative_order links
        );
        return allowedTypes.includes('narrative_order');
      });

      // Include both the selected source and valid targets in candidates
      setCandidates([selectedSourceId, ...validTargets.map((a) => a._id)]);
    } else {
      // No source selected yet: show ALL apps as potential sources (green)
      setCandidates(apps.map((a) => a._id));
    }
  }, [selectedSourceId, apps, links, setPrimaryActionMode]);

  // Handle click on background: create new Stickie and link it (Phase 1B)
  const handleBackgroundClick = (e: React.MouseEvent<SVGElement>) => {
    // Only create if a source is selected
    if (!selectedSourceId) {
      // No source selected: do nothing (user must select source first)
      return;
    }

    if (!user) {
      toast({
        title: 'User Not Found',
        description: 'Cannot create Stickie: user information unavailable.',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // Get click position in board coordinates using the cursor utility
    const { x, y } = getBoardCursor();
    const boardX = x;
    const boardY = y;

    // Find current path to determine step number
    const sourceApp = apps.find((a) => a._id === selectedSourceId);
    if (!sourceApp) return;

    // Validate maxOutgoing constraint before creating new app
    const outgoingNarrativeLinks = links.filter(
      (l) => l.data.sourceAppId === selectedSourceId && l.data.type === 'narrative_order'
    );
    if (outgoingNarrativeLinks.length >= 1) {
      toast({
        title: 'Cannot Create Link',
        description: 'This app already has an outgoing narrative link (max: 1 for linear paths).',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    const currentPath = getNarrativePath(selectedSourceId, links);
    const nextStepNumber = currentPath.length + 1;

    // Create Stickie at click location
    const newApp = setupApp(
      `Step ${nextStepNumber}`,
      'Stickie',
      boardX,
      boardY,
      roomId,
      boardId,
      { w: 400, h: 420 },
      { color: user.data.color || 'yellow', text: `Step ${nextStepNumber}` }
    );

    // Create the app and link it
    createApp(newApp).then((response) => {
      if (response && response.data && response.data._id) {
        const newAppId = response.data._id;

        // Link source to new Stickie
        addLink(selectedSourceId, newAppId, boardId, 'narrative_order');

        toast({
          title: 'Step Created',
          description: `Added Step ${nextStepNumber} to presentation path.`,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });

        // Select the new Stickie as source for next link
        setSelectedSourceId(newAppId);
        // Also update the link store's linkedAppId for CursorArrow to follow correctly
        clearLinkAppId();
        cacheLinkedAppId(newAppId);

        // Update candidates to show valid targets from new source
        const validTargets = apps.filter((targetApp) => {
          if (targetApp._id === newAppId) return false;
          const allowedTypes = getAllowedLinkTypes(
            response.data,
            targetApp,
            links,
            'narrative_order'
          );
          return allowedTypes.includes('narrative_order');
        });
        setCandidates([newAppId, ...validTargets.map((a) => a._id)]);
      }
    }).catch((err) => {
      toast({
        title: 'Creation Failed',
        description: 'Could not create Stickie app.',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
      console.error('[NarrativeLinkerMode] Failed to create app:', err);
    });
  };

  // Handle click on an app rectangle
  const handleClick = (e: React.MouseEvent<SVGRectElement>, appId: string) => {
    e.stopPropagation();

    // 1) No source yet: pick source
    if (!selectedSourceId) {
      // For narrative mode, any app can be a source (not just SageCells)
      const sourceApp = apps.find((a) => a._id === appId);
      if (!sourceApp) return;

      // Check if this app has already reached maxOutgoing for narrative_order links
      const outgoingNarrativeLinks = links.filter(
        (l) => l.data.sourceAppId === appId && l.data.type === 'narrative_order'
      );
      if (outgoingNarrativeLinks.length >= 1) {
        // maxOutgoing = 1 for narrative_order
        toast({
          title: 'Cannot Start Link',
          description: 'This app already has an outgoing narrative link (max: 1).',
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
        return;
      }

      // Allow selection even if no existing apps are valid targets
      // (background-click will create a new valid target)
      setSelectedSourceId(appId);
      // Also update store for CursorArrow
      clearLinkAppId();
      cacheLinkedAppId(appId);
      // Compute targets for this source
      const tgts = apps.filter((t) => {
        if (t._id === appId) return false; // Exclude self
        const allowedTypes = getAllowedLinkTypes(sourceApp, t, links, 'narrative_order');
        return allowedTypes.includes('narrative_order');
      });

      // Include both the selected source and valid targets in candidates
      setCandidates([appId, ...tgts.map((t) => t._id)]);
      return;
    }

    // 2) Clicking same source: deselect
    if (selectedSourceId === appId) {
      setSelectedSourceId('');
      setCandidates([]);
      clearLinkAppId(); // Also clear store when deselecting
      return;
    }

    // 3) Add link: validate target
    if (!candidates.includes(appId)) {
      toast({
        title: 'Invalid Target',
        description: 'Cannot link to this app (maxIncoming reached or would create cycle).',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // Create narrative_order link
    const src = apps.find((a) => a._id === selectedSourceId)!;
    const tgt = apps.find((a) => a._id === appId)!;
    const allowed = getAllowedLinkTypes(src, tgt, links, 'narrative_order');

    if (!allowed.includes('narrative_order')) {
      toast({
        title: 'No Link Available',
        description: 'Cannot create narrative link between these apps.',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    } else {
      // Create narrative_order link
      addLink(selectedSourceId, appId, src.data.boardId, 'narrative_order');

      toast({
        title: 'Narrative Link Created',
        description: `Added ${src.data.type} → ${tgt.data.type} to presentation path.`,
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });

      // Auto-advance: set the newly linked target as the new source
      // This enables click-click-click rapid authoring workflow
      setSelectedSourceId(appId);
      // Also update the link store's linkedAppId for CursorArrow to follow correctly
      clearLinkAppId();
      cacheLinkedAppId(appId);
      // candidates will be recomputed by the useEffect above
    }
  };

  // ESC key: exit narrative linker mode
  useHotkeys('esc', () => {
    setSelectedSourceId('');
    setCandidates([]);
    clearLinkAppId(); // Clear store when exiting mode
    if (onExit) {
      onExit();
    }
  });

  // 'N' key: also exit narrative linker mode (same as ESC)
  useHotkeys('n', () => {
    setSelectedSourceId('');
    setCandidates([]);
    clearLinkAppId(); // Clear store when exiting mode
    if (onExit) {
      onExit();
    }
  });

  return (
    <svg
      width={boardWidth}
      height={boardHeight}
      style={{ position: 'absolute', top: 0, left: 0, zIndex: 100000, pointerEvents: 'none' }}
    >
      {/* Background area - clicks here create new Stickies and auto-link them */}
      <rect
        x={0}
        y={0}
        width={boardWidth}
        height={boardHeight}
        fill="transparent"
        style={{ pointerEvents: 'all' }}
        onClick={handleBackgroundClick}
      />

      {/* App overlay rectangles */}
      {boxes.map(({ id, position, size }) => {
        const isCandidate = candidates.includes(id);
        const isSelected = selectedSourceId === id;

        let color, fill;
        if (isSelected) {
          color = teal;
          fill = teal + '40';
        } else if (isCandidate) {
          color = green;
          fill = green + '40';
        } else {
          return null; // Don't render overlay for non-candidates
        }

        return (
          <rect
            key={id}
            x={position.x}
            y={position.y}
            width={size.width}
            height={size.height}
            stroke={color}
            strokeWidth={Math.max(1, 1.5 / scale)}
            fill={fill}
            onClick={(e) => handleClick(e, id)}
            style={{ pointerEvents: 'auto' }}
          />
        );
      })}
    </svg>
  );
}
